import { Chip, ChipProps, styled } from '@mui/material';
import { FilterParams } from '@synergy/models';
import { ReactElement, ReactNode } from 'react';

import { useFilter } from '../filter-context';

export const FilterToggleChip = styled(Chip, {
    shouldForwardProp: prop => prop !== 'isSelected' && prop !== 'outlined' && prop !== 'active',
})<{
    isSelected?: boolean;
    outlined?: boolean;
    active?: boolean;
}>(({ theme, isSelected, outlined, color, active }) => {
    const paletteName = !color || color === 'default' ? 'secondary' : color;
    const paletteColor = theme.palette[paletteName];

    return {
        ...theme.typography.button,
        textTransform: 'uppercase',
        color: theme.palette.grey[800],
        borderColor: theme.palette.grey[400],

        '&&:hover': {
            color: outlined ? 'inherit' : paletteColor.contrastText,
            borderColor: paletteColor.main,
            background: outlined ? 'inherit' : paletteColor.main,
        },

        ...(isSelected && {
            background: outlined ? 'inherit' : paletteColor.main,
            borderColor: paletteColor.main,
            color: outlined ? paletteColor.main : paletteColor.contrastText,

            '&&:hover': {
                background: paletteColor.dark,
                borderColor: outlined ? 'inherit' : paletteColor.dark,
            },
        }),

        ...(active && {
            borderColor: theme.palette.primary.main,
        }),

        '& .MuiChip-label': {
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2),
        },

        '.MuiChip-icon': {
            marginLeft: theme.spacing(2),
        },
    };
});

export interface FilterToggleProps extends ChipProps {
    label: ReactNode;
    filterParam: FilterParams;
    outlined?: boolean;
    active?: boolean;
}

export const FilterToggle = (props: FilterToggleProps): ReactElement => {
    const { label, filterParam, outlined = false, active = false, ...chipProps } = props;
    const { updateFilter, filter } = useFilter();

    const handleClick = (): void => {
        if (filter.get(filterParam)) {
            filter.delete(filterParam);
        } else {
            filter.set(filterParam, 'true');
        }
        updateFilter(filter);
    };

    return (
        <FilterToggleChip
            label={label}
            variant="outlined"
            outlined={outlined}
            onClick={handleClick}
            isSelected={filter.get(filterParam) ? true : false}
            active={active}
            {...chipProps}
        />
    );
};
